import { type ForwardedRef, forwardRef } from 'react';

import { cva } from 'cva';
import { Collection } from 'react-aria-components/Collection';
import {
  GridList,
  GridListLoadMoreItem,
  type GridListProps,
} from 'react-aria-components/GridList';

import styles from './card_view.module.css';

/**
 * Allow using generics with forwardRef.
 * Borrowed from https://oida.dev/typescript-react-generic-forward-refs/
 **/
declare module 'react' {
  // biome-ignore lint/suspicious/noShadow: forwardRef generic hack
  function forwardRef<T, P>(
    render: (props: P, ref: React.Ref<T>) => React.ReactNode | null,
  ): (props: P & React.RefAttributes<T>) => React.ReactNode | null;
}

const cardViewVariants = cva({
  base: styles.card_view,
});

export type CardViewProps<T> = GridListProps<T> & {
  onLoadMore?: () => void;
};

/**
 * CardView - Displays a grid of interactive card items, with support for
 * keyboard navigation, single or multiple selection, and row actions.
 *
 * See `CardItem` for more details on how to create a card item.
 *
 * See https://react-spectrum.adobe.com/react-aria/GridList.html for more details.
 *
 * ```tsx
 * <CardView items={items}>
 *   {(item) => (
 *     <MyCustomCardItem id={item.id} />
 *   )}
 * </CardView>
 * ```
 */
export const CardView = forwardRef(
  <T extends object>(
    props: CardViewProps<T>,
    ref: ForwardedRef<HTMLDivElement>,
  ) => {
    const { children, className, items, ...rest } = props;

    const classes = cardViewVariants({
      className,
    });

    return (
      <div
        id="card-view-container-wrapper"
        ref={ref}
        className={styles.card_view_container}
      >
        <GridList layout="grid" {...rest} className={classes}>
          <Collection items={items}>{children}</Collection>
          {props.onLoadMore ? (
            <GridListLoadMoreItem onLoadMore={props.onLoadMore} />
          ) : null}
        </GridList>
      </div>
    );
  },
);
